// Minimal fake Dusk wallet provider, adapted from @dusk/connect's own
// examples/reference-wallet fixture (references/repos/connect). Announces
// itself over the same window events a real browser-extension wallet uses
// (dusk:announceProvider / dusk:requestProvider), so the dApp code under
// test never knows the difference. Lets us design and exercise every
// connect-flow state without installing the real Dusk Wallet extension.

const REQUEST_PROVIDER_EVENT = "dusk:requestProvider";
const ANNOUNCE_PROVIDER_EVENT = "dusk:announceProvider";

function hexOf(value) {
  return `0x${[...new TextEncoder().encode(String(value || ""))]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("")}`;
}

/** Decode dusk_signMessage params the way the real extension's toBytes does. */
function coerceMessageBytes(value) {
  if (value instanceof Uint8Array) return value;
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  if (Array.isArray(value)) return new Uint8Array(value);
  if (typeof value === "string") {
    let s = value.trim();
    if (s.startsWith("0x") || s.startsWith("0X")) s = s.slice(2);
    if (!s || s.length % 2 !== 0 || !/^[0-9a-fA-F]+$/.test(s)) {
      throw new Error("mock dusk_signMessage: message must be bytes");
    }
    const out = new Uint8Array(s.length / 2);
    for (let i = 0; i < out.length; i++) {
      out[i] = parseInt(s.slice(i * 2, i * 2 + 2), 16);
    }
    return out;
  }
  throw new Error("mock dusk_signMessage: message must be bytes");
}

function svgIcon(letters, color) {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
      <rect width="64" height="64" rx="12" fill="${color}" />
      <text x="50%" y="55%" dominant-baseline="middle" text-anchor="middle"
        font-family="ui-monospace, SFMono-Regular, Menlo, monospace"
        font-size="20" font-weight="600" fill="#0b0b0f">${letters}</text>
    </svg>
  `;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/**
 * @param {{ uuid: string, name: string, color: string, account: string, balance: string, rejectConnect?: boolean, connectDelayMs?: number }} config
 */
export function createMockWallet(config) {
  const info = {
    uuid: config.uuid,
    name: config.name,
    icon: svgIcon(config.name.slice(0, 2).toUpperCase(), config.color),
    rdns: config.uuid,
  };

  const listeners = new Map();
  let authorized = false;
  let txCounter = 0;
  const chainId = "dusk:2";
  const nodeUrl = "https://testnet.nodes.dusk.network";

  const emit = (name, payload) => {
    for (const handler of listeners.get(name) ?? []) handler(payload);
  };
  const on = (name, handler) => {
    const set = listeners.get(name) ?? new Set();
    set.add(handler);
    listeners.set(name, set);
  };
  const off = (name, handler) => listeners.get(name)?.delete(handler);

  const currentProfiles = (includeShielded = false) =>
    authorized
      ? [
          {
            profileId: "profile:0",
            account: config.account,
            ...(includeShielded
              ? { shieldedAddress: `${config.account.slice(0, 24)}-shielded` }
              : {}),
          },
        ]
      : [];

  const provider = {
    isDusk: true,
    get chainId() {
      return chainId;
    },
    get profiles() {
      return currentProfiles();
    },
    get isAuthorized() {
      return authorized;
    },
    async request({ method, params }) {
      switch (method) {
        case "dusk_getCapabilities":
          return {
            provider: info.rdns,
            walletVersion: "0.0.0-mock",
            chainId,
            nodeUrl,
            networkName: "Testnet",
            methods: [
              "dusk_getCapabilities",
              "dusk_requestProfiles",
              "dusk_profiles",
              "dusk_requestShieldedAddress",
              "dusk_chainId",
              "dusk_getPublicBalance",
              "dusk_sendTransaction",
              "dusk_signMessage",
              "dusk_citadelOwnedLicenses",
              "dusk_signCitadelUseLicense",
              "dusk_disconnect",
            ],
            txKinds: ["transfer", "contract_call"],
            limits: { maxFnArgsBytes: 65536, maxFnNameChars: 64, maxMemoBytes: 512 },
            features: {
              shieldedRead: false,
              shieldedRecipients: true,
              shieldedReceiveAddress: true,
              signMessage: true,
              signAuth: false,
              contractCallPrivacy: false,
              watchAsset: false,
              citadelOwnedLicenses: true,
              citadelUseLicense: true,
            },
          };

        case "dusk_profiles":
          return currentProfiles();

        case "dusk_requestProfiles": {
          if (config.connectDelayMs) {
            await new Promise((resolve) => setTimeout(resolve, config.connectDelayMs));
          }
          if (config.rejectConnect) {
            throw Object.assign(new Error("User rejected the request"), { code: 4001 });
          }
          authorized = true;
          emit("connect", { chainId });
          const includeShielded = Boolean(params?.shieldedReceiveAddress);
          const profiles = currentProfiles(includeShielded);
          emit("profilesChanged", profiles);
          return profiles;
        }

        case "dusk_requestShieldedAddress":
          return {
            address: `${config.account.slice(0, 24)}-shielded`,
            profileId: "profile:0",
            account: config.account,
            chainId,
          };

        case "dusk_chainId":
          return chainId;

        case "dusk_getPublicBalance":
          if (!authorized) throw Object.assign(new Error("Origin is not connected"), { code: 4100 });
          return { nonce: "3", value: config.balance };

        // NOT a real BLS signature - just a deterministic hex fixture keyed
        // on this mock wallet's account + the signed message, so trading
        // key derivation (`deriveTradingKeyFromWallet`) reproduces the same
        // fake key across sessions the same way a real wallet's signature
        // would. Never verifiable against the mock's (nonexistent) pubkey.
        case "dusk_signMessage": {
          if (!authorized) throw Object.assign(new Error("Origin is not connected"), { code: 4100 });
          // Match real extension: accept hex / number[] / Uint8Array (portal
          // sends 0x-hex so JSON-serialized RPC params survive the bridge).
          const messageBytes = coerceMessageBytes(params?.message);
          const messageHex = Array.from(messageBytes)
            .map((b) => b.toString(16).padStart(2, "0"))
            .join("");
          return {
            account: config.account,
            origin: window.location.origin,
            chainId,
            messageHash: hexOf(`mock-hash:${messageHex}`),
            messageLen: messageBytes.length,
            signature: hexOf(`mock-sig:${config.account}:${messageHex}`),
            payload: hexOf(messageHex),
          };
        }

        case "dusk_sendTransaction":
          if (!authorized) throw Object.assign(new Error("Origin is not connected"), { code: 4100 });
          txCounter += 1;
          await new Promise((resolve) => setTimeout(resolve, 600));
          // 64 hex chars - shape a real tx hash so UI validation passes.
          // Gateway verify against the live node will still fail for mocks.
          const hash = Array.from({ length: 64 }, (_, i) =>
            ((txCounter * 17 + i * 3) % 16).toString(16)
          ).join("");
          return { hash, nonce: String(100 + txCounter) };

        case "dusk_citadelOwnedLicenses": {
          if (!authorized) throw Object.assign(new Error("Origin is not connected"), { code: 4100 });
          const candidates = params?.candidates ?? [];
          // Dev-mode stand-in for real Citadel proving (see forks/wallet's
          // citadelDriver.js for the real, wasm-backed implementation this
          // fixture doesn't run): "owns" every candidate it's shown.
          return candidates.map((c) => ({
            position: c.position,
            attrData: hexOf(`attr:${c.position}`),
            issuer: hexOf(`issuer:${c.position}`),
          }));
        }

        case "dusk_signCitadelUseLicense": {
          if (!authorized) throw Object.assign(new Error("Origin is not connected"), { code: 4100 });
          // Fabricated but well-shaped response so the dApp flow (prove ->
          // submit -> derive session id) can be exercised quickly in dev
          // mode. Not a real proof - see forks/wallet + citadel-witness-driver
          // in sme_platform for the real ~218s wasm-backed implementation.
          await new Promise((resolve) => setTimeout(resolve, 800));
          const seed = `${params?.licenseBlob ?? ""}:${params?.pkLp ?? ""}:${params?.pkSp ?? ""}:${params?.challenge ?? ""}`;
          return {
            proof: hexOf(`proof:${seed}`),
            publicInputs: [0, 1, 2, 3, 4, 5, 6, 7].map((i) => hexOf(`pi${i}:${seed}`)),
            sessionCookie: hexOf(`cookie:${seed}`),
            contractArgs: hexOf(`contractArgs:${seed}`),
          };
        }

        case "dusk_disconnect":
          authorized = false;
          emit("disconnect", { code: 4900, message: "Disconnected" });
          emit("profilesChanged", []);
          return true;

        default:
          throw Object.assign(new Error(`Unsupported method: ${method}`), { code: 4200 });
      }
    },
    on,
    once(name, handler) {
      const wrapped = (payload) => {
        off(name, wrapped);
        handler(payload);
      };
      on(name, wrapped);
    },
    off,
    removeListener: off,
    removeAllListeners(name) {
      if (name) listeners.delete(name);
      else listeners.clear();
    },
    isConnected: () => true,
  };

  const announce = () => {
    window.dispatchEvent(new CustomEvent(ANNOUNCE_PROVIDER_EVENT, { detail: { info, provider } }));
  };

  window.addEventListener(REQUEST_PROVIDER_EVENT, announce);

  return { info, announce };
}

// A real, funded testnet Moonlight account (base58-encoded 96-byte BLS
// public key) - see references/testnet-wallet.md ("safe to share - public,
// not secret"). Used as the mock wallet's account instead of a made-up
// string: `BlsPublicKey` contract arguments (bidder/owner/recipient) are
// deserialized as a real BLS12-381 curve point, not just checked for byte
// length, so an arbitrary 96-byte string (even a correctly-sized one)
// fails with "InvalidData" - confirmed empirically via
// `contract.encode('commitment_info', [...])` while fixing this. Only a
// genuinely valid public key round-trips, which is what every write call
// that takes a `bidder`/`owner` argument needs to encode successfully.
const REAL_TESTNET_ACCOUNT =
  "zoMdB5Bs5DGrnsALy2NagpHZi7kgzp1dYCcTS43bjBPC2i8zrDaLjKk6Z4WEMjBcDTFr6sZHuLoHKXehkMwVJMhVMWTdGTyvRf3tU4pfwuuePSkRrBQn75v7Lvv9CrmkgYX";

/** Installs the standard two-wallet demo fixture (a normal wallet + one that always rejects). */
export function installDemoWallets() {
  const good = createMockWallet({
    uuid: "dev.mock.duskwallet",
    name: "Dusk Wallet (mock)",
    color: "#7c5cff",
    account: REAL_TESTNET_ACCOUNT,
    balance: "48200000000",
    connectDelayMs: 700,
  });
  const flaky = createMockWallet({
    uuid: "dev.mock.piewallet",
    name: "Piewallet (mock, always rejects)",
    color: "#ff8a3d",
    // Never actually used as a bidder identity - this wallet's connect()
    // always rejects, so no BlsPublicKey-argument call is ever reachable
    // through it. Left as an obviously-fake placeholder is fine here.
    account: "dusk1mocksecondaccount111111111111111111111111111111111",
    balance: "0",
    rejectConnect: true,
    connectDelayMs: 400,
  });
  good.announce();
  flaky.announce();
  return { good, flaky };
}
